/**
 * Admin account lifecycle (issue #5) - typed wrapper over `GET /accounts`,
 * and `PATCH /accounts/{id}`. Account provisioning belongs exclusively to
 * the invitation saga.
 *
 * This client makes no authority claim of its own. Both routes are behind
 * `verify_admin` and tenant-scoped twice over, so the server is the gate and a
 * caller who reaches these functions without admin membership is refused
 * regardless of what the interface offered.
 *
 * Provisioning is deliberately absent. Admin, instructor, and student account
 * creation all go through the invitation API and its database authority gates.
 *
 * `list` enumerates every membership in the tenant, admins and deactivated rows
 * included, so a caller no longer needs a `user_id` handed to it from somewhere
 * else before it can act. Each row is named by the membership's person, the
 * name every surface shows (ADR 0034); the login account's own name is never
 * used. A nameless row still carries its role and state.
 */
import { api } from "@/lib/api/client";
import { commitOptions } from "@/lib/api/commitSeams";

export type AccountRole = "student" | "instructor";

/** What `PATCH` echoes back. Wider than `AccountRole` because the route can be
 *  aimed at any membership in the tenant, including an admin's. */
export type MembershipRole = AccountRole | "admin";

export type RetirementReasonCategory =
  | "legacy_test_identity"
  | "relationship_ended"
  | "duplicate_or_error"
  | "policy_or_security_action"
  | "other";

export interface AccountRetirement {
  retired_at: string;
  retired_by: string;
  reason_category: RetirementReasonCategory;
  note: string;
}

export interface RetirementBlocker {
  kind: "self" | "tenant_super_admin" | "admin_authority" | "student_standing" | "active_enrollment" | "teaching_assignment";
  label: string;
  related_id?: string;
  course_id?: string;
  status?: string;
  assignment_kind?: string;
  href?: string;
  /** A student standing's person, who holds the blocking record (ADR 0034). */
  person_id?: string;
}

export interface AccountRetirementReadiness {
  user_id: string;
  eligible: boolean;
  role: MembershipRole;
  is_active: boolean;
  is_retired: boolean;
  is_super_admin: boolean;
  provider_posture: "managed" | "observed_existing_admin" | "unmanaged";
  blockers: RetirementBlocker[];
}

export interface AccountLifecycleReason {
  reason_category: RetirementReasonCategory;
  note: string;
}

export interface AccountListRow {
  user_id: string;
  role: MembershipRole;
  is_active: boolean;
  /** Tenant-scoped capability on an admin membership, never a fourth role. */
  is_super_admin: boolean;
  created_at: string;
  /** The person's full name; null until the registrar names the person. */
  display_name: string | null;
  /** The login's sign-in address, else the person's contact email. */
  email: string | null;
  /** Present only where the member also has a student record. */
  sid: string | null;
  retirement?: AccountRetirement | null;
  managed_state?: ManagedIdentityState | null;
  observed_existing_workspace_admin?: ObservedExistingWorkspaceAdminState | null;
}

export interface ObservedExistingWorkspaceAdminState {
  /** Existing institution-owned Google administrator. LMS lifecycle is local only. */
  last_verified_at: string | null;
}

export interface ManagedIdentityState {
  desired: "active" | "suspended";
  observed: "unknown" | "active" | "suspended" | "archived" | "missing";
  incomplete: boolean;
  recovery_required: boolean;
  /** When the exact-ID readback last agreed with `desired`. Null means Google
   *  has never been read back for this binding, which is not the same as
   *  "observed active" and must not be shown as one. Nothing re-reads Google
   *  between a page load and the reconciler's next run, so every Workspace
   *  claim on screen is as old as this timestamp. */
  last_verified_at: string | null;
}

/** Tenant-level managed-identity counts.
 *
 *  `suspended` exists because suspension is not seat reclamation: a suspended
 *  Google Workspace user keeps consuming its paid license, so this number only
 *  grows until a retention disposition decides what happens to those accounts.
 *  An admin who cannot see it finds out from a bill. */
export interface ManagedIdentitySummary {
  managed: number;
  suspended: number;
  incomplete: number;
  /** Bound identities whose Workspace account could not be observed at the
   *  immutable id IMAM holds. No automated path repairs these. */
  unresolved: number;
  oldest_verification: string | null;
  suspension_reclaims_license: false;
}

export interface AccountActivation {
  user_id: string;
  role: MembershipRole;
  is_active: boolean;
  is_super_admin: boolean;
  managed_state: ManagedIdentityState | null;
  observed_existing_workspace_admin?: ObservedExistingWorkspaceAdminState | null;
  retirement?: AccountRetirement | null;
}

/**
 * The one key the roster page and the per-account control both read.
 *
 * Shared rather than repeated because the control is rendered on two surfaces
 * and a `PATCH` from either has to move both: one key means one cache entry,
 * one fetch, and no window in which a page shows a state the page beside it has
 * already changed.
 */
export const ACCOUNTS_QUERY_KEY = ["accounts"] as const;
export const MANAGED_IDENTITY_SUMMARY_QUERY_KEY = ["accounts", "managed-identity-summary"] as const;

export const accountsApi = {
  /** Every membership in the tenant, admins and deactivated rows included,
   *  sorted by display name with unnamed rows last. Unpaged and unsearched -
   *  the client facets it, as the other record pages do. */
  list: () => api.get<AccountListRow[]>("/accounts"),

  /** Deactivation takes effect on the account's next request, because
   *  membership is read live; the live GoTrue session kill is best-effort
   *  hardening on top of that, not the block itself. The server refuses an
   *  admin's own id with 400 and an id it cannot find in the tenant with 404. */
  setActive: (accountId: string, isActive: boolean, key: string) =>
    api.patch<AccountActivation>(`/accounts/${accountId}`, { is_active: isActive }, commitOptions(key)),

  retirementReadiness: (accountId: string) =>
    api.get<AccountRetirementReadiness>(`/accounts/${accountId}/retirement-readiness`),

  /** Each lifecycle change is one commit: its key lets Check read whether it landed (W1). */
  retire: (accountId: string, reason: AccountLifecycleReason, key: string) =>
    api.post<AccountActivation>(`/accounts/${accountId}/retire`, reason, commitOptions(key)),

  restore: (accountId: string, reason: AccountLifecycleReason, key: string) =>
    api.post<AccountActivation>(`/accounts/${accountId}/restore`, reason, commitOptions(key)),

  /** Tenant-level managed-identity counts, including the suspended-seat count
   *  that makes license growth visible before a bill does. */
  managedIdentitySummary: () =>
    api.get<ManagedIdentitySummary>("/accounts/managed-identity-summary"),
};
